import { useSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import { isOkthxCalendarId } from "@/types/calendarEvent.ts";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { type Calendar, getCalendars } from "./api.ts";

function parseCalendarIds(str: string): string[] {
  try {
    const parsed = JSON.parse(str);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export interface UseVisibleCalendarIdsResult {
  /** All connected calendars; `undefined` while the list is still loading. */
  calendars: Calendar[] | undefined;
  /**
   * IDs the user has chosen to display in the Calendar UI (toggled in the
   * Calendar page aside). Falls back to the availability set
   * (`selectedCalendarIds`) when the user has not made an explicit visibility
   * choice. `undefined` when no preference exists at all (events query defaults
   * to the backend's availability set); `[]` when the user has explicitly
   * hidden every calendar.
   */
  visibleCalendarIds: string[] | undefined;
  /** Calendars present in the account but hidden — drives the CalendarList toggle UI. */
  hiddenCalendarIds: Set<string>;
  /**
   * Pass to `useCalendarSurface` / `useCalendarEvents`. Tri-state:
   * - `null` while the calendar list is still loading — defers the events
   *   query so the first fetch doesn't go out with no `calendarIds` filter
   *   (which the backend silently treats as the availability set, the wrong
   *   source for surfaces that mirror the user's calendar UI visibility).
   * - `[]` when the user has explicitly hidden every calendar — short-circuits
   *   the events query to zero results without hitting the network.
   * - `string[]` for the resolved visible set, or `undefined` when no
   *   preference exists and the backend default is appropriate.
   */
  calendarIdsForEvents: string[] | null | undefined;
}

/**
 * Reads the user's "visible in calendar UI" selection, falling back to the
 * availability set when no explicit visibility choice has been made. Used by
 * every calendar surface — the full-screen `/calendar` page and the
 * right-sidebar day view — so they stay in sync.
 */
export function useVisibleCalendarIds(): UseVisibleCalendarIdsResult {
  const calendarPrefs = useSurfacePreferences("calendars");
  const { data: calendars } = useQuery<Calendar[]>({
    queryKey: ["calendars"],
    queryFn: getCalendars,
  });

  return useMemo(() => {
    if (!calendars || calendars.length === 0) {
      return {
        calendars,
        visibleCalendarIds: undefined,
        hiddenCalendarIds: new Set<string>(),
        calendarIdsForEvents: calendars === undefined ? null : undefined,
      };
    }

    const visibleStr = calendarPrefs.visibleCalendarIds;
    const selectedStr = calendarPrefs.selectedCalendarIds;
    // `visibleStr` is set means the user has expressed an explicit visibility
    // choice on the Calendar page — including the explicit "hide all" state
    // produced by toggling off the last visible calendar. We must distinguish
    // that from "user has never chosen", which falls back to the availability
    // set, so the two states don't both collapse to "show availability events".
    const hasExplicitVisibility = !!visibleStr;

    // With no explicit choice the grid follows the availability set — but an
    // okthx calendar is deliberately absent from that set, since it holds no
    // busy time. Add it here rather than writing it server-side, which would
    // turn "no choice" into an explicit one and freeze the fallback.
    //
    // Only when that set is non-empty. An empty availability set means "no
    // filter, show everything" a few lines below; appending the reminders
    // calendar to it would turn that into a one-calendar filter and hide every
    // provider event on the grid.
    let visibleIds: string[];
    if (hasExplicitVisibility) {
      visibleIds = parseCalendarIds(visibleStr);
    } else {
      const selectedIds = parseCalendarIds(selectedStr);
      const okthxIds = calendars
        .filter((c) => isOkthxCalendarId(c.calendarId))
        .map((c) => c.calendarId);
      visibleIds = selectedIds.length > 0 ? [...selectedIds, ...okthxIds] : [];
    }

    if (visibleIds.length === 0) {
      if (hasExplicitVisibility) {
        return {
          calendars,
          visibleCalendarIds: [],
          hiddenCalendarIds: new Set(calendars.map((c) => c.calendarId)),
          calendarIdsForEvents: [],
        };
      }
      return {
        calendars,
        visibleCalendarIds: undefined,
        hiddenCalendarIds: new Set<string>(),
        calendarIdsForEvents: undefined,
      };
    }

    const visibleSet = new Set(visibleIds);
    const hidden = new Set<string>();
    for (const cal of calendars) {
      if (!visibleSet.has(cal.calendarId)) {
        hidden.add(cal.calendarId);
      }
    }
    return {
      calendars,
      visibleCalendarIds: visibleIds,
      hiddenCalendarIds: hidden,
      calendarIdsForEvents: visibleIds,
    };
  }, [calendars, calendarPrefs.visibleCalendarIds, calendarPrefs.selectedCalendarIds]);
}
